import React from 'react';
import type { Bill } from '../../types/billing.types';
import { BILL_STATUS_LABELS } from '../../types/billing.types';

interface Props {
  bill: Bill;
  isEditable: boolean;
}

const InvoiceOverviewTab: React.FC<Props> = ({ bill, isEditable }) => {
  const fmtDate = (d: string | null | undefined) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const fmtCurrency = (v: number) => `$${Number(v || 0).toFixed(2)}`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Client & Matter Info */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4">
          Client & Matter
        </h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Client</dt>
            <dd className="font-medium text-gray-900 dark:text-white">
              {(bill.matter as any)?.client?.client_name || '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Matter</dt>
            <dd className="font-medium text-gray-900 dark:text-white">
              {(bill.matter as any)?.description || (bill.matter as any)?.matter_number || '—'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Matter Number</dt>
            <dd className="font-medium text-gray-900 dark:text-white">
              {(bill.matter as any)?.matter_number || '—'}
            </dd>
          </div>
        </dl>
      </div>

      {/* Financial Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4">
          Financial Summary
        </h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Subtotal</dt>
            <dd className="font-medium text-gray-900 dark:text-white">{fmtCurrency(bill.subtotal)}</dd>
          </div>
          {Number(bill.discount_amount) > 0 && (
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Discounts</dt>
              <dd className="font-medium text-red-600 dark:text-red-400">-{fmtCurrency(bill.discount_amount)}</dd>
            </div>
          )}
          {Number(bill.tax_amount) > 0 && (
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Tax</dt>
              <dd className="font-medium text-gray-900 dark:text-white">{fmtCurrency(bill.tax_amount)}</dd>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
            <dt className="font-semibold text-gray-900 dark:text-white">Total</dt>
            <dd className="font-bold text-gray-900 dark:text-white">{fmtCurrency(bill.total)}</dd>
          </div>
          {Number(bill.amount_paid) > 0 && (
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Amount Paid</dt>
              <dd className="font-medium text-green-600 dark:text-green-400">-{fmtCurrency(bill.amount_paid)}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="font-semibold text-gray-900 dark:text-white">Balance Due</dt>
            <dd className={`font-bold ${Number(bill.balance) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
              {fmtCurrency(bill.balance)}
            </dd>
          </div>
        </dl>
      </div>

      {/* Dates & Status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4">
          Dates & Status
        </h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Status</dt>
            <dd className="font-medium text-gray-900 dark:text-white">{BILL_STATUS_LABELS[bill.status]}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Issue Date</dt>
            <dd className="font-medium text-gray-900 dark:text-white">{fmtDate(bill.issue_date)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Due Date</dt>
            <dd className="font-medium text-gray-900 dark:text-white">{fmtDate(bill.due_date)}</dd>
          </div>
          {(bill.period_start || bill.period_end) && (
            <div className="flex justify-between">
              <dt className="text-gray-500 dark:text-gray-400">Billing Period</dt>
              <dd className="font-medium text-gray-900 dark:text-white">
                {fmtDate(bill.period_start)} — {fmtDate(bill.period_end)}
              </dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-gray-500 dark:text-gray-400">Created</dt>
            <dd className="font-medium text-gray-900 dark:text-white">{fmtDate(bill.created_at)}</dd>
          </div>
        </dl>
      </div>

      {/* Description */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4">
          Description
        </h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
          {bill.description || 'No description provided.'}
        </p>
      </div>
    </div>
  );
};

export default InvoiceOverviewTab;
